/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { cssMap, cx, jsx } from '@atlaskit/css'; // eslint-disable-line @typescript-eslint/no-unused-vars
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import FadeIn from '@atlaskit/motion/fade-in';
import SlideIn from '@atlaskit/motion/slide-in';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Suspense from '@atlaskit/react-ufo/suspense';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { A2UISurface } from '@atlassian/a2ui-renderer';
import { type UnconnectedApp } from '@atlassian/conversation-assistant-chat-prompt-input/app-connection-banner';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import { InThreadAppConnectionPrompt } from '@atlassian/conversation-assistant-chat-prompt-input/InThreadAppConnectionPrompt';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { useSmartLinkConnector } from '@atlassian/conversation-assistant-chat-prompt-input/useSmartLinkConnector';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { createSkillAdf } from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { isAssistantMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { isMarathonAgent } from '@atlassian/conversation-assistant-service/common/utils/is-marathon-agent';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	getRateLimitedActionType,
	isFullScreenError,
	isRateLimited,
	useChatSignals,
	useChatSignalsActions,
} from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	type AnswerNowStatus,
	CHAT_STORE_STATUS,
	isAnswerNowAvailable,
	isStoreAssistant,
	isStoreHuman,
	type StoreAssistant,
	type StoreChatItem,
	type StoreFollowUp,
	type StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import type { ConnectorState } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	type EmptyChatLaunchProjectContext,
	type SelectedPinnedItem,
	type SelectedSpace,
	useEmptyChatLaunchSelector,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { usePlaceholderStore } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { useSmoothScrollAnchor } from '@atlassian/conversation-assistant-ui-components/controllers/useSmoothScrollAnchor';
import { LoadingState } from '@atlassian/conversation-assistant-ui-components/ui/LoadingState';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';
import type { ChatEmptyLaunchParams } from '@atlassian/rovo-for-you/types';
import { usePersonalityThinkingVerb } from '@atlassian/rovo-personalization/personality-thinking-verb/usePersonalityThinkingVerb';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import {
	type ExpandableTrace,
	MessageLoading,
} from '@atlassian/rovo-platform-ui-components/loading-state/MessageLoading';
import { PROMPT_LIBRARY_SKILL_TAG } from '@atlassian/rovo-skills/constants';
import { SkillMetadataProvider } from '@atlassian/rovo-skills/services/skill-metadata-provider';

import { usePinnedAppConnectionPrompt } from '../../common/hooks/usePinnedAppConnectionPrompt';
import { usePromptConnectorConnectedEffects } from '../../common/hooks/usePromptConnectorConnectedEffects';
import { ErrorBoundary } from '../../common/ui/error-boundary';
import { DEFAULT_AGENT_DISPLAY_NAME } from '../../common/utils/DEFAULT_AGENT_DISPLAY_NAME';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useSidebarActionsStaggerAnimation } from '../../common/utils/use-animation-delay/useSidebarActionsStaggerAnimation';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import type { ScreenType } from '../../common/utils/use-calculated-screen-type';
import { useWindowLocation } from '../../common/utils/use-window-location/useWindowLocation';
import { useActiveAction } from '../../controllers/active-action';
import { useChatMessageResearchActions } from '../../controllers/chat-message-research-store/useChatMessageResearchActions';
import { useMessageIdsWithResearchData } from '../../controllers/chat-message-research-store/useMessageIdsWithResearchData';
import { useRoundedResearch } from '../../controllers/chat-message-research-store/useRoundedResearch';
import { findLastIndex } from '../../controllers/chat-store/findLastIndex';
import { getInteractionModeExecutedFromStoreAssistant } from '../../controllers/chat-store/getInteractionModeExecutedFromStoreAssistant';
import { interactionModeExecutedAttributes } from '../../controllers/chat-store/interactionModeExecutedAttributes';
import { useChatStore } from '../../controllers/chat-store/useChatStore';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../controllers/conversation/useConversation';
import { useResendMessage } from '../../controllers/resend-message';
import { ConversationAssistantUIModes } from '../../types';
import { hasStreamingAction } from '../../utils/hasStreamingAction';
import { filterChatMessages } from '../../utils/message-filters';
import { usePlaceholderOverrideDispatch } from '../chat-input-placeholder-override-context/usePlaceholderOverrideDispatch';
import { ConversationTitleBlockContainer } from '../conversation-title-block/conversation-title-block';
import { SidebarPromptInlineBrowse } from '../prompt-library/sidebar-prompt-inline-browse';
import { InsideRovodalContext } from '../rovodal-host/insideRovodalContext';
import { useViewDeadAirContext } from '../view-dead-air-tracker/useViewDeadAirContext';
import { ViewDeadAirTracker } from '../view-dead-air-tracker/ViewDeadAirTracker';

import { A2uiSurfaceEntryPointRenderer } from './A2uiSurfaceEntryPointRenderer';
import { buildRespondedAgentIdByHumanMessageId } from './build-responded-agent-id-by-human-message-id';
import { type ChatHistoryMessageRenderers, ChatTranscript } from './ChatTranscript';
import { ConversationActionContainer } from './ConversationActionContainer';
import { getLastHumanMessageContentRenderVersion } from './last-human-message-content-render-version';
import { MessageDeepResearchV2Refresh } from './message-research-refresh/MessageDeepResearchV2Refresh';
import { MessageLoadingPaddedContainer } from './message-research-refresh/MessageLoadingPaddedContainer';
import { MessageThinkDeeperResearch } from './message-research-refresh/MessageThinkDeeperResearch';
import messages from './messages';
import { RovoEmptyStateLoading } from './rovo-empty-state-loading';
import { type RovoTabId, SUGGESTIONS_TAB_ID } from './rovo-tabs/constants';
import { SharedChatContinuationPill } from './shared-chat-continuation-pill/SharedChatContinuationPill';
import { shouldHideLiveThinkingIndicator as computeShouldHideLiveThinkingIndicator } from './should-hide-live-thinking-indicator';
import { SkillsInlineBrowse } from './skills-inline-browse';
import { StoreAssistantView } from './store-assistant';
import { AgentResponseHat } from './store-assistant/agent-response-hat';
import { deriveBaselineAuthorIdentity } from './store-assistant/deriveBaselineAuthorIdentity';
import { deriveResponseAuthorIdentity } from './store-assistant/deriveResponseAuthorIdentity';
import { type ResponseAuthorIdentity } from './store-assistant/ResponseAuthorIdentity';
import { StoreMemoryUpdateView } from './store-assistant/store-memory-update';
import { useResponseHatState } from './store-assistant/useResponseHatState';
import { StoreConnectorRecommendation } from './store-connector-recommendation/StoreConnectorRecommendation';
import { StoreDeepResearchRateLimitWarning } from './store-deep-research-rate-limit-warning';
import { StoreErrorView } from './store-error';
import { StoreFollowUpView } from './store-follow-up';
import { StoreGuardBlockView } from './store-guard-block';
import { RedactionEnforcementMessage } from './store-human/redacted-prompt/RedactionEnforcementMessage';
import { StoreHumanView } from './store-human/StoreHumanView';
import { StoreRateLimitWarning } from './store-rate-limit-warning';
import { useA2UIAction } from './use-a2ui-action';
import { useEmptyStateRender } from './use-empty-state-render';
import { useLatestMessageRenderStabilization } from './use-latest-message-render-stabilization';
import { useMessageGroups } from './use-message-groups';
import { useStreamingThinkingAgent } from './use-streaming-thinking-agent';
import { useVirtualizationState } from './use-virtualization-state';
import { useLayoutInitialBottomAnchor } from './useLayoutInitialBottomAnchor';

// Stable empty list so the connect-confirmation hook's deps don't churn when no
// 3P connection prompt is pinned.
const NO_APP_CONNECTION_PROMPT_APPS: UnconnectedApp[] = [];

const styles = cssMap({
	container: {
		height: '100%',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		position: 'relative',
	},

	scrollingDiv: {
		width: '100%',
		overflow: 'auto',
		paddingTop: token('space.0'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},

	animationTransitionContainerFS: {
		width: '100%',
	},
	animationTransitionContainer: {
		width: '100%',
		height: '100%',
	},
	// Mini modal: use flex:1 instead of height:100% to work with flex parent during resize
	animationTransitionContainerMiniModal: {
		width: '100%',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
	},

	containerFlex: {
		flex: 1,
	},
});

const HideWhenThinkDeeperInProgress = ({
	messageId,
	children,
}: {
	messageId: string;
	children: React.ReactNode;
}) => {
	const { isThinkingDeeperRunning } = useRoundedResearch({ messageId });

	if (isThinkingDeeperRunning) {
		return <MessageLoadingPaddedContainer />;
	}

	return <>{children}</>;
};

type ActiveResponseHatHandoffProps = {
	storeAssistant: StoreAssistant;
	previousAssistantAuthor?: ResponseAuthorIdentity;
	shouldEnableAgentResponseHat: boolean;
	isConversationActivelyGenerating: boolean;
	canRenderAfterThinking: boolean;
	hasAnswerContent: boolean;
};

const ActiveResponseHatHandoff = ({
	storeAssistant,
	previousAssistantAuthor,
	shouldEnableAgentResponseHat,
	isConversationActivelyGenerating,
	canRenderAfterThinking,
	hasAnswerContent,
}: ActiveResponseHatHandoffProps): React.JSX.Element | null => {
	const { assistanceServiceConfig, experienceId, productHeader, siteId } = useGlobalConfig();
	const assistanceServiceParams = useMemo(
		() => ({
			baseUrl: assistanceServiceConfig?.baseUrl,
			headers: assistanceServiceConfig?.headers,
			channelId: assistanceServiceConfig?.channelId,
			experienceId,
			product: productHeader,
			siteId,
			touchpointSource: productHeader,
		}),
		[
			assistanceServiceConfig?.baseUrl,
			assistanceServiceConfig?.headers,
			assistanceServiceConfig?.channelId,
			experienceId,
			productHeader,
			siteId,
		],
	);
	const responseHatState = useResponseHatState({
		storeAssistant,
		previousAssistantAuthor,
		shouldEnableAgentResponseHat: shouldEnableAgentResponseHat && isConversationActivelyGenerating,
		isConversationActivelyGenerating,
		canRenderAfterThinking,
		hasAnswerContent,
		assistanceServiceParams,
	});

	if (!responseHatState.shouldRender) {
		return null;
	}

	return (
		<AgentResponseHat
			testId="active-response-hat-handoff"
			isDefaultRovo={responseHatState.isDefaultRovo}
			agentIdentityAccountId={responseHatState.identityAccountId}
			isIdentityHydrating={responseHatState.isHydrating}
		/>
	);
};

function isValidProjectContext(value: unknown): value is EmptyChatLaunchProjectContext {
	if (value == null || typeof value !== 'object') {
		return false;
	}
	const obj = value as Record<string, unknown>;
	// A valid context requires a non-empty ID and name.
	// projectUrl is optional — Rovo Spaces do not have a URL.
	return (
		typeof obj.projectId === 'string' &&
		obj.projectId !== '' &&
		typeof obj.projectName === 'string' &&
		obj.projectName !== ''
	);
}

export type InteractiveChatHistoryControllerProps = {
	conversationId: string;
	mode?: ConversationAssistantUIModes;
	handleClickDynamicPrompt?: (starter: ConversationAction) => void;
	screenType: ScreenType;
	closeInlineBrowse: () => void;
	isResizeEnabled?: boolean;
	isResizeHeaderThresholdReached?: boolean | null;
	onDiscoverPromptLibraryClick?: () => void;
	product?: string;
	onOpenMemoryManagement?: () => void;
	/** Called when the active tab changes. Used by main.tsx to hide the chat input on the "for-you" tab. */
	onActiveTabChange?: (tabId: string) => void;
	/**
	 * When provided, controls which tab is visually selected in RovoTabs and drives
	 * the chat input visibility in main.tsx. This is the single source of truth for tab state —
	 * it flows down to RovoTabs as a controlled prop and is synced back up via onActiveTabChange.
	 * Also used by main.tsx to reset to suggestions when navigating back (onResetEmptyStateTab).
	 */
	activeTabId?: RovoTabId;
	selectedSpace: SelectedSpace | undefined;
	selectedPinnedItem: SelectedPinnedItem | undefined;
};

export const InteractiveChatHistoryController = ({
	conversationId,
	mode,
	handleClickDynamicPrompt,
	screenType,
	closeInlineBrowse,
	isResizeEnabled = false,
	isResizeHeaderThresholdReached = false,
	onDiscoverPromptLibraryClick,
	product,
	onOpenMemoryManagement,
	onActiveTabChange,
	activeTabId: activeTabIdProp,
	selectedSpace,
	selectedPinnedItem,
}: InteractiveChatHistoryControllerProps): JSX.Element | null => {
	di(
		fg,
		useConversation,
		useChatStore,
		useUserPreferences,
		useChatModeState,
		StoreAssistantView,
		StoreErrorView,
		StoreHumanView,
		useChatMessages,
		MessageDeepResearchV2Refresh,
	);

	const setChatInputPlaceholderOverride = usePlaceholderOverrideDispatch();

	const { siteId, profileData } = useGlobalConfig();
	const [{ customGreeting: runtimeCustomGreeting }] = useEmptyChatLaunchSelector();
	const { setAIFeatureContext } = useChatContextStoreActions();
	const {
		setActiveTabId,
		setPreviousTabId,
		setEmptyChatLaunch,
		setSelectedSpace,
		setSelectedPinnedItem,
		setActiveMenu,
	} = useUIStoreActions();
	const { setChatMode } = useChatModeStoreActions();
	const { fullScreen, elements } = useUIConfig();
	// Cheap store read — used together with the streaming + experiment gate
	// checks below to decide whether to mount the connector recommendation card.
	// Only when all conditions pass do we instantiate StoreConnectorRecommendation,
	// which sets up the OAuth flow.
	const recommendationConnector = useSmartLinkConnector();
	const { conversation, selectedAgent: conversationAgent } = useConversation({
		conversationId,
	});

	// Clear placeholder override when a message is sent
	useEffect(() => {
		if (conversation?.status === CHAT_STORE_STATUS.awaitingResponse) {
			setChatInputPlaceholderOverride(undefined);
		}
	}, [conversation?.status, setChatInputPlaceholderOverride]);

	const [, { setPlaceholder }] = usePlaceholderStore();
	const { setAnalyticsPropertiesWithoutRerender } = useAnalyticsPropertiesContext();
	const { trackAIMAUEvent, sendAnalyticsEvent } = useAnalytics();

	const handleSkillSelect = useCallback(
		(skill: Skill) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoChatSkillSelected',
				attributes: {
					slashCommand: skill.slug,
					skillProductKey: skill.product,
					skillSelectionSource: 'inlineBrowse',
				},
			});
			// Record the selection source on the AnalyticsPropertiesContext so the chat
			// input can attach it to the next `aiResult viewed` event.
			setAnalyticsPropertiesWithoutRerender({ skillSelectionSource: 'inlineBrowse' });
			setPlaceholder({
				prompt: '',
				adfDocument: createSkillAdf(skill),
				type: 'generic',
				selectionStartIndex: 1,
				selectionEndIndex: 1,
				insertAtCursor: true,
			});
			closeInlineBrowse();
		},
		[setPlaceholder, setAnalyticsPropertiesWithoutRerender, closeInlineBrowse, sendAnalyticsEvent],
	);

	const { getChat, sendMessage, answerNow, setSelectedConversation } = useChatStoreActions();

	/** Handles "Chat" click on a space card, or on a pinned Jira Epic/Atlas Goal/Atlas Project —
	 * writes entity context to stores and, for spaces only, navigates to the space landing page. */
	const handleChatEmptyLaunch = useCallback(
		(params: ChatEmptyLaunchParams) => {
			const projectContext: EmptyChatLaunchProjectContext = params.selectedPinnedItem
				? {
						projectId: params.selectedPinnedItem.ari,
						projectName: params.selectedPinnedItem.name,
						projectUrl: params.selectedPinnedItem.url,
					}
				: {
						projectId: params.spaceId,
						projectName: params.spaceName,
						projectUrl: '',
						spaceId: params.spaceId,
					};

			const validProjectContext = isValidProjectContext(projectContext)
				? projectContext
				: undefined;

			// Space-scoped launches suppress `customGreeting` to match `handleSelectSpace`
			// in `rovo-side-navigation` / `browse-spaces-view`. Otherwise the chat-header
			// back button short-circuits on `isEmptyChatLaunchActive` and forces users to
			// click back twice — once to clear the greeting, once to exit the space.
			const isSpaceScopedLaunch = !params.selectedPinnedItem && Boolean(params.spaceId);

			// Write project context to the chat-context store so that
			// sendMessage includes it in the backend request payload
			setAIFeatureContext({
				contextKey: 'projectContext',
				setContext: <T,>(_: T): T => (validProjectContext ?? {}) as T,
			});

			// Set the space bar in the chat input — same state as clicking a space in the nav menu.
			// `sources` is forwarded so create-space-from-thread launches seed the originating
			// thread's sources onto the space immediately (for display), matching the side-nav
			// create flow (`rovo-side-navigation` → `handleSelectSpace`). For a pinned Epic/Goal/
			// Project, set the pinned-entity slot instead — the two are mutually exclusive, so
			// each branch clears the other to avoid stale state from a prior launch.
			if (params.selectedPinnedItem) {
				setSelectedPinnedItem(params.selectedPinnedItem);
				setSelectedSpace(undefined);
			} else {
				setSelectedSpace({
					id: params.spaceId,
					name: params.spaceName,
					emoji: params.spaceEmoji,
					sources: params.sources,
					entryPoint: 'zeroQuery',
				});
				setSelectedPinnedItem(undefined);
			}

			setEmptyChatLaunch({
				...(!isSpaceScopedLaunch && params.greetingTitle
					? {
							customGreeting: {
								title: params.greetingTitle,
								iconSrc: params.iconSrc,
								iconSrcDark: params.iconSrcDark,
							},
						}
					: {}),
				customConversationActions: params.customConversationActions ?? [],
				replaceProviderActions: params.replaceProviderActions,
				projectContext: validProjectContext,
				skillRegistryTags: params.skillRegistryTags,
			});

			if (
				isSpaceScopedLaunch &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
			) {
				// Save the tab the user was on when they launched the space chat so
				// `resetToPreviousTab()` (called from the header's back button) can
				// restore it. Without this, back navigation from a For You-launched
				// space chat falls back to whatever `previousTabId` held at launch
				// time — typically 'suggestions' — instead of returning to For You.
				const launchTabId = params.launchTabId ?? SUGGESTIONS_TAB_ID;
				setPreviousTabId(launchTabId);
				setActiveTabId(launchTabId);
				setActiveMenu('space-landing');
			} else {
				if (params.selectedPinnedItem) {
					// A pinned-entity launch stays on activeMenu: 'none' (no dedicated landing
					// page), but still needs activeTabId reset the same way the space-landing
					// branch above does — otherwise it's left on whatever tab was active before
					// the click (e.g. For You), RovoTabs renders that stale tab instead of the
					// Suggestions tab actions render under, and the starter cards are unclickable.
					const launchTabId = params.launchTabId ?? SUGGESTIONS_TAB_ID;
					setPreviousTabId(launchTabId);
					setActiveTabId(launchTabId);
				}
				setActiveMenu('none');
			}

			// Set the context banner preference for this conversation.
			setChatMode(EMPTY_CONVERSATION_ID, {
				useCurrentPageContext: elements?.shouldShowContextBanner !== false,
			});

			setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
		},
		[
			elements?.shouldShowContextBanner,
			setAIFeatureContext,
			setSelectedSpace,
			setSelectedPinnedItem,
			setActiveTabId,
			setPreviousTabId,
			setEmptyChatLaunch,
			setActiveMenu,
			setChatMode,
			setSelectedConversation,
		],
	);
	const sidebarActionsStaggerAnimation = useSidebarActionsStaggerAnimation();

	// Apply mini modal resize styles when in mini modal mode and resize experiment is enabled
	const useMiniModalStyles = mode === ConversationAssistantUIModes.MINI_MODAL && isResizeEnabled;
	const { formatMessage } = useIntl();
	const [{ conversationError }] = useChatSignals();
	const isFullScreenErrorState = isFullScreenError(conversationError);
	const isRateLimitedState = isRateLimited(conversationError);
	const rateLimitedActionType = getRateLimitedActionType(conversationError);
	const { setConversationError } = useChatSignalsActions();

	const [rawConversationMessages] = useChatMessages({ conversationId });

	// Apply message filters to remove unwanted messages (e.g. "Action executed")
	const conversationMessages = useMemo(() => {
		return filterChatMessages(rawConversationMessages);
	}, [rawConversationMessages]);
	const conversationMessagesResponseCount =
		!conversation ||
		!Array.isArray(rawConversationMessages) ||
		(conversation.status === CHAT_STORE_STATUS.loadingChat && rawConversationMessages.length === 0)
			? null
			: rawConversationMessages.length;
	const currentChatMode = useChatModeState(conversationId);
	const { activeAction, clearActiveAction } = useActiveAction();
	const currentLocation = useWindowLocation();
	const isConversationAgentDefault = !conversationAgent || Boolean(conversationAgent.is_default);

	const { thinkingAgent, isThinkingAgentHydrating } = useStreamingThinkingAgent({
		conversation,
		conversationMessages,
	});
	const isThinkingAgentDefault = !thinkingAgent || thinkingAgent?.is_default;
	const { messageIdSelectedForPreview } = useStagingAreaState();
	const { clearTargetTaskMessageId } = useChatStoreActions();
	const targetTaskMessageId = conversation?.targetTaskMessageId ?? null;
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();
	const { getMessageResearchById } = useChatMessageResearchActions();
	// Cheap Set<messageId> derived once per store update — avoids N× getMessageResearchById
	// calls in renderMessage for long conversation histories.
	const messageIdsWithResearchData = useMessageIdsWithResearchData();

	const chatHistoryContainerRef = useRef<HTMLDivElement>(null);
	const { scrollToBottom, scrollToLatestMessage, maintainScrollHeight } = useSmoothScrollAnchor({
		containerRef: chatHistoryContainerRef,
	});
	const lastMessageGroupRef = useRef<HTMLDivElement>(null);
	const hasMounted = useRef(false);
	const isRenderStabilizationEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;
	const shouldUseStableOlderMessageRenderer = isRenderStabilizationEnabled;

	// A Rovodal-embedded chat shares the global store with the main chat, so re-fetching the
	// already-loaded conversation on mount just blanks the list and flashes a "Reloading chat"
	// spinner — skip it.
	const isInsideRovodal =
		React.useContext(InsideRovodalContext) &&
		FeatureGates.getExperimentValue<boolean>(
			'rovo_jira_skip_refetch_embedded_rovodal_chat',
			'isEnabled',
			false,
		) === true;

	const isSharedChatContinuationPillEnabled = FeatureGates.getExperimentValue(
		'rovo_conversation_sharing',
		'isEnabled',
		false,
	);
	const messageGroups = useMessageGroups(conversationMessages, isRenderStabilizationEnabled);
	const sharedChatContinuationPillTarget = useMemo(
		() =>
			isSharedChatContinuationPillEnabled
				? SharedChatContinuationPill.getTarget(conversationMessages)
				: undefined,
		[conversationMessages, isSharedChatContinuationPillEnabled],
	);
	const olderMessageGroupsCount = Math.max(0, messageGroups.length - 1);
	const olderMessageGroups = useMemo(
		() => (messageGroups.length > 1 ? messageGroups.slice(0, -1) : []),
		[messageGroups],
	);

	const pendingScrollToMessageIdRef = useRef<string | null>(null);
	const isDeepLinkPending = !!targetTaskMessageId || !!pendingScrollToMessageIdRef.current;
	const [scrollToBottomGeneration, setScrollToBottomGeneration] = useState(0);
	const [liveRegionText, setLiveRegionText] = useState(
		fg('rovo_chat_trace_aria_live_updates') ? '' : undefined,
	);

	const { isVirtualizationEnabled } = useVirtualizationState({
		conversationId,
		conversationMessagesCount: conversationMessages.length,
		conversationMessagesResponseCount,
		isDeepLinkPending,
		olderMessageGroupsCount,
	});
	useLayoutInitialBottomAnchor({
		enabled: !isVirtualizationEnabled && messageGroups.length > 0 && !isDeepLinkPending,
		conversationId,
		totalSize: conversationMessages.length,
		containerRef: chatHistoryContainerRef,
		scrollToBottomGeneration,
	});

	// Keep track of whether the skills screen has been opened once to avoid re-rendering the skills component
	const hasOpenedSkillsOnceRef = useRef(false);
	if (screenType === 'skills') {
		hasOpenedSkillsOnceRef.current = true;
	}

	// Keep track of whether the prompt library screen has been opened once
	const hasOpenedPromptLibraryOnceRef = useRef(false);
	if (screenType === 'prompt-library') {
		hasOpenedPromptLibraryOnceRef.current = true;
	}

	const humanMessages = useMemo(
		() => conversationMessages.filter((msg) => isStoreHuman(msg)) as StoreHuman[],
		[conversationMessages],
	);
	const lastHumanMessage: StoreHuman | undefined =
		humanMessages && humanMessages.length > 0 ? humanMessages[humanMessages.length - 1] : undefined;
	const lastHumanMessageId = lastHumanMessage?.content?.id
		? String(lastHumanMessage?.content?.id)
		: 'NO_HUMAN_MESSAGE';
	const lastHumanMessageContent = lastHumanMessage?.content?.content;
	const lastHumanMessageContentRenderVersion = useMemo(
		() => getLastHumanMessageContentRenderVersion(lastHumanMessageContent),
		[lastHumanMessageContent],
	);
	// Constant for the whole render; used by the edit-pencil gate below to extend
	// the affordance to "send didn't complete" terminal states (user Stop,
	// network/server error, browser-killed background request).
	const lastSendWasInterrupted =
		conversation?.lastAgentMessageStatus === 'CANCELLED' ||
		conversation?.lastAgentMessageStatus === 'FAILED';
	const firstSessionAssistantMessage = useMemo(
		() =>
			conversationMessages.find((msg) => isStoreAssistant(msg) && !msg.isHistorical) as
				| StoreAssistant
				| undefined,
		[conversationMessages],
	);
	const firstSessionAssistantMessageId =
		firstSessionAssistantMessage?.content?.message?.message?.id;
	const shouldEnableAgentResponseHat = isExperimentEnabled('rovo_chat_mention_agents');
	// Gate-on uses the routed streaming author from `useStreamingThinkingAgent`.
	// Gate-off preserves the previous conversation/header-agent label behavior.
	const thinkingLabelAgent = shouldEnableAgentResponseHat ? thinkingAgent : conversationAgent;
	const isThinkingLabelAgentDefault = !thinkingLabelAgent || thinkingLabelAgent.is_default;
	// The mention-agent experiment controls both response hats and greying out
	// inactive agent @mentions in sent prompts.
	const shouldEnableInactiveMentionGreying = shouldEnableAgentResponseHat;
	// Read once per render (not once per message inside renderMessage) so the Statsig exposure event fires at most once per ChatHistory render.
	const isEditLastUserPromptEnabled = FeatureGates.getExperimentValue(
		'rovo_chat_edit_last_user_prompt_exp',
		'isEnabled',
		false,
	);
	// Response hats mark author transitions between assistant messages. The first
	// assistant response has no previous assistant author and therefore no hat.
	const tracesForLoading = useMemo<ExpandableTrace[]>(
		() =>
			conversation?.traces?.map(
				(trace): ExpandableTrace => ({
					title: trace.messageContent,
					status: trace?.status ?? 'success',
				}),
			) ?? [],
		[conversation?.traces],
	);

	// The assistant message the live thinking indicator is rendered for. Used to
	// announce the same dynamic thinking text via the ARIA live region below.
	const lastAssistantMessage = useMemo<StoreAssistant | undefined>(() => {
		const lastAssistantIndex = findLastIndex(conversationMessages, isStoreAssistant);
		return lastAssistantIndex >= 0
			? (conversationMessages[lastAssistantIndex] as StoreAssistant)
			: undefined;
	}, [conversationMessages]);

	const previousAssistantAuthorByMessageId = useMemo(() => {
		const previousByMessageId = new Map<string, ResponseAuthorIdentity>();
		if (!shouldEnableAgentResponseHat) {
			return previousByMessageId;
		}

		let previousAssistantAuthor: ResponseAuthorIdentity | undefined;
		for (const message of conversationMessages) {
			if (!isStoreAssistant(message)) {
				continue;
			}

			if (previousAssistantAuthor) {
				previousByMessageId.set(message.content.message.message.id, previousAssistantAuthor);
			}
			previousAssistantAuthor = deriveResponseAuthorIdentity(message);
		}

		return previousByMessageId;
	}, [conversationMessages, shouldEnableAgentResponseHat]);

	// Maps each human message to the agent that responded to it, paired by
	// document order. Used to grey out inactive agent @mentions in sent prompts.
	// Gated behind the `rovo_chat_mention_agents` experiment via
	// `shouldEnableInactiveMentionGreying`.
	const respondedAgentIdByHumanMessageId = useMemo(
		() =>
			buildRespondedAgentIdByHumanMessageId(
				conversationMessages,
				shouldEnableInactiveMentionGreying,
			),
		[conversationMessages, shouldEnableInactiveMentionGreying],
	);

	// Personality thinking-verb, gated by the `rovo_chat_personality_presets` and
	// `rovo_chat_personality_thinking_verb` experiments. One verb is chosen per turn
	// (keyed off the human message being answered) and rendered by both the main
	// thinking indicator (`getRovoThinkingState`) and the research paths, so the label
	// stays consistent within a spell and across reasoning modes. Gate-off or no
	// preset resolves to `undefined`, and every path keeps its existing fallback.
	const personalityThinkingServiceParams = useAssistanceServiceParams({
		touchpointSource: 'chat-history',
	});
	const isPersonalityThinkingVerbEligible =
		(conversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
			conversation?.status === CHAT_STORE_STATUS.typingResponse) &&
		!isThinkingAgentHydrating &&
		isThinkingLabelAgentDefault;
	const { verbMessage: personalityThinkingVerb } = usePersonalityThinkingVerb({
		enabled: isPersonalityThinkingVerbEligible,
		spellId: lastHumanMessageId === 'NO_HUMAN_MESSAGE' ? undefined : lastHumanMessageId,
		assistanceServiceParams: personalityThinkingServiceParams,
	});
	// Pre-resolved for the research path, which renders the default Rovo verb only.
	const personalityThinkingText = personalityThinkingVerb
		? formatMessage(personalityThinkingVerb)
		: undefined;

	const getRovoThinkingState = (
		message: StoreAssistant,
		// Pass null to indicate "caller fetched, but there is no research data".
		// Pass undefined only when no fetch has been attempted (e.g. the thinkingText call site).
		messageResearch?: ReturnType<typeof getMessageResearchById> | null,
	) => {
		const messageId = message.content?.message?.message?.id;
		// When messageResearch is explicitly null the caller already confirmed no data exists —
		// skip the store lookup. When undefined, fall back to fetching (thinkingText call site).
		const resolvedMessageResearch =
			messageResearch !== undefined
				? messageResearch
				: messageId
					? getMessageResearchById({ messageId })
					: undefined;

		const hasThinkDeeperTriggered = resolvedMessageResearch?.hasThinkDeeperTriggered ?? false;

		const isResearchStoreInitialized =
			Object.keys(resolvedMessageResearch?.researchPlans ?? {}).length > 0 ||
			Object.keys(resolvedMessageResearch?.steps ?? {}).length > 0;

		const thinkDeeperTriggered =
			hasThinkDeeperTriggered || currentChatMode.reasoningMode === 'think-deeper';
		let thinkingAgentName = DEFAULT_AGENT_DISPLAY_NAME;
		if (!isThinkingLabelAgentDefault) {
			thinkingAgentName = thinkingLabelAgent.name?.trim() || DEFAULT_AGENT_DISPLAY_NAME;
		}

		let thinkingText = '';
		if (!isThinkingAgentHydrating) {
			if (isThinkingLabelAgentDefault && personalityThinkingText) {
				// Personality flavors the default Rovo verb only; custom agents keep
				// "{agentName} is thinking".
				thinkingText = personalityThinkingText;
			} else if (thinkDeeperTriggered) {
				thinkingText = formatMessage(messages.thinkDeeperThinking);
			} else {
				thinkingText = formatMessage(messages.rovoIsThinking, { name: thinkingAgentName });
			}
		}

		return {
			hasThinkDeeperTriggered,
			isResearchStoreInitialized,
			thinkingText,
			researchCompleted: resolvedMessageResearch?.researchCompleted ?? false,
		};
	};

	const viewDeadAirBroker = useViewDeadAirContext();
	useEffect(() => {
		return () => {
			viewDeadAirBroker?.finish('unmounted');
		};
	}, [viewDeadAirBroker]);

	const previousLastHumanMessageId = useRef<string | null | 'NO_HUMAN_MESSAGE'>(null);
	const previousChatStatus = useRef<CHAT_STORE_STATUS | undefined>(undefined);
	const previousMessagesLength = useRef(0);
	const lastConversationIdThatHasChatLoaded = useRef<string | undefined>(undefined);

	// The connector recommendation pinned for this chat-history render. Set on
	// the first non-null connector for the current conversation so the card
	// can't unmount on success, swap apps, or appear later in the render
	// lifecycle.
	const [pinnedRecommendationConnector, setPinnedRecommendationConnector] = useState<{
		conversationId: string;
		connector: ConnectorState;
	} | null>(null);
	const recommendationConnectorForRender =
		pinnedRecommendationConnector?.conversationId === conversationId
			? pinnedRecommendationConnector.connector
			: null;

	// Pin the first non-null connector recommendation seen during this render
	// lifecycle (see state declaration above for full rationale).
	useEffect(() => {
		if (!recommendationConnector) {
			return;
		}

		setPinnedRecommendationConnector((previousConnector) =>
			previousConnector?.conversationId === conversationId
				? previousConnector
				: { conversationId, connector: recommendationConnector },
		);
	}, [conversationId, recommendationConnector]);

	// The 3P suggested-prompts connection prompt's apps, latched per conversation
	// so the in-thread prompt survives new messages and can't render in another
	// conversation (see the hook for the full rationale).
	const appConnectionPromptAppsForRender = usePinnedAppConnectionPrompt(conversationId);

	// Post-connect effects (confirmation message + enabling the app in the sources
	// filter), keyed off the same latched apps so they keep firing after the banner
	// store is cleared.
	usePromptConnectorConnectedEffects({
		conversationId,
		apps: appConnectionPromptAppsForRender ?? NO_APP_CONNECTION_PROMPT_APPS,
	});

	useEffect(() => {
		if (conversationId) {
			lastConversationIdThatHasChatLoaded.current = conversationId;
			if (targetTaskMessageId) {
				pendingScrollToMessageIdRef.current = targetTaskMessageId;
				clearTargetTaskMessageId({ conversationId });
			}
			getChat({
				conversationId,
				messageIdSelectedForPreview,
				...((isRenderStabilizationEnabled || isInsideRovodal) &&
				!targetTaskMessageId &&
				!messageIdSelectedForPreview
					? { skipIfConversationAlreadyLoaded: true }
					: {}),
			});
		}
	}, [
		conversationId,
		getChat,
		isRenderStabilizationEnabled,
		isInsideRovodal,
		messageIdSelectedForPreview,
		targetTaskMessageId,
		clearTargetTaskMessageId,
	]);

	let finalScreenType = screenType;
	if (!conversation || !conversationMessages.length) {
		if (
			// even though the chat status is not loading yet, if there is a change in conversationId,
			// we know we are going to be in loading state because of chat loading effect above,
			// so stick to loading state to begin with instead of starting with empty and then jumping to loading
			lastConversationIdThatHasChatLoaded.current !== conversationId &&
			conversationId !== EMPTY_CONVERSATION_ID
		) {
			finalScreenType = 'loading';
		}
	}

	useEffect(() => {
		// Scroll when user enter the new message in the same conversation
		const shouldScrollToNextMessage =
			conversation?.status === CHAT_STORE_STATUS.awaitingResponse &&
			previousLastHumanMessageId.current !== lastHumanMessageId;

		// Scroll to bottom when user enter the conversation for the first time,
		// unless we have a specific target message to scroll to.
		const isTransitioningToIdle =
			previousChatStatus.current === CHAT_STORE_STATUS.loadingChat &&
			conversation?.status === CHAT_STORE_STATUS.idle;
		const shouldScrollToBottom = isTransitioningToIdle && !pendingScrollToMessageIdRef.current;

		previousLastHumanMessageId.current = lastHumanMessageId;
		previousChatStatus.current = conversation?.status;

		if (isTransitioningToIdle && pendingScrollToMessageIdRef.current) {
			const targetId = pendingScrollToMessageIdRef.current;
			pendingScrollToMessageIdRef.current = null;
			setTimeout(() => {
				const el = chatHistoryContainerRef.current?.querySelector(
					`[data-message-id="${targetId}"]`,
				);
				if (el) {
					el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				} else {
					scrollToBottom({ smooth: false });
				}
			}, 0);
			return;
		}

		if (!shouldScrollToNextMessage && !shouldScrollToBottom) {
			return;
		}
		if (isVirtualizationEnabled) {
			setScrollToBottomGeneration((prev) => prev + 1);
		}
		scrollToBottom({
			smooth: shouldScrollToNextMessage,
		});
	}, [
		conversationId,
		lastHumanMessageId,
		conversation?.status,
		scrollToBottom,
		isVirtualizationEnabled,
	]);

	// Scroll to the top of latest message when status changes to typingResponse
	useEffect(() => {
		if (conversation?.status !== CHAT_STORE_STATUS.typingResponse) {
			return;
		}
		scrollToLatestMessage({ smooth: true });
	}, [conversation?.status, scrollToLatestMessage]);

	// The dynamic thinking text shown on screen for the last assistant message
	// (e.g. "Thinking deeply and analyzing" or "{agent} is thinking"). Kept in a
	// ref so the live-region effect can announce the same text on the start
	// transition without re-running (and re-announcing) on every render.
	//
	// When a last assistant message exists we mirror exactly what
	// `getRovoThinkingState` renders — including the intentionally empty string
	// while the thinking agent is hydrating, so we never misattribute the turn to
	// the default "Rovo" name. Only when there is no assistant message yet (the
	// indicator is not derived from any message) do we fall back to the default
	// so the start of a turn is still announced.
	const thinkingText =
		lastAssistantMessage && fg('rovo_chat_trace_aria_live_updates')
			? getRovoThinkingState(lastAssistantMessage).thinkingText
			: formatMessage(messages.rovoIsThinking, { name: DEFAULT_AGENT_DISPLAY_NAME });
	const thinkingTextRef = useRef(thinkingText);
	thinkingTextRef.current = thinkingText;

	// Announce AI response generation status to screen readers via an ARIA live region.
	// Only two transitions are announced (start and finish) to avoid overloading
	// screen readers during streaming. Focus is never moved here.
	const isGeneratingRef = useRef(false);
	useEffect(() => {
		if (!fg('rovo_chat_trace_aria_live_updates')) {
			return;
		}

		const isGenerating =
			conversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
			conversation?.status === CHAT_STORE_STATUS.typingResponse;

		if (isGenerating && !isGeneratingRef.current) {
			// Announce the thinking text rendered on screen, falling back to a generic
			// message if the agent name hasn't hydrated yet (which would produce an
			// empty string and silently drop the screen reader announcement).
			setLiveRegionText(
				thinkingTextRef.current ||
					formatMessage(messages.rovoIsThinking, { name: DEFAULT_AGENT_DISPLAY_NAME }),
			);
		} else if (!isGenerating && isGeneratingRef.current) {
			setLiveRegionText(formatMessage(messages.responseComplete));
		}

		isGeneratingRef.current = isGenerating;
	}, [conversation?.status, formatMessage]);

	// Maintain scroll position when a follow-up message is rendered
	useEffect(() => {
		const currentLength = conversationMessages.length;
		if (currentLength > previousMessagesLength.current) {
			maintainScrollHeight();
		}
		previousMessagesLength.current = currentLength;
	}, [conversationMessages, maintainScrollHeight]);

	useEffect(() => {
		//Only run action reset on location change, not first mount
		if (hasMounted.current) {
			clearActiveAction();
		} else {
			hasMounted.current = true;
		}

		return () => {
			clearActiveAction();
		};
	}, [currentLocation, clearActiveAction]);

	const handleResendMessage = useResendMessage(conversationId);
	const handleEditAndResendMessage = useResendMessage(conversationId, { mode: 'edit' });

	const { handleA2UIAction } = useA2UIAction({
		conversationId,
		agentId: !conversationAgent ? undefined : conversationAgent?.id,
		webSearchEnabled: currentChatMode.webSearchEnabled,
		useCurrentBrowsingContext: currentChatMode.useCurrentPageContext,
		appFilters: currentChatMode.appFilters,
	});

	const handleAuthLinkClicked = useCallback(
		(authType: string, messageId: string) => {
			const messageIndex = conversationMessages.findIndex(
				(msg) => isAssistantMessage(msg) && msg.id === messageId,
			);
			const lastAgentMessageIndex = findLastIndex(conversationMessages, isAssistantMessage);

			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'actionAuthLink',
				attributes: {
					authType,
					agentMessageIndex: messageIndex,
					lastAgentMessageIndex,
				},
			});
		},
		[conversationMessages, sendAnalyticsEvent],
	);

	useEffect(() => {
		if (finalScreenType === 'conversation') {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'existingRovoChatConversation',
			});
		}
		// Should only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Inline actionable follow-ups (parsed from the `◈` marker in the assistant
	// response) are not `StoreFollowUp` items, so they submit via this lighter
	// handler rather than `onFollowUpClicked`. Chat-mode inheritance mirrors the
	// store follow-up behaviour (deep research / think-deeper are not inherited).
	const onInlineFollowUpClicked = useCallback(
		(prompt: string) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoChatActionableFollowUpClicked',
				attributes: {
					interactionSource: 'followUps',
					followUpSource: 'ai-mate',
				},
			});
			sendMessage({
				agent: conversationAgent,
				prompt: encodeAdf(prompt),
				conversationId,
				storeUserMessages,
				messageSources: {
					interactionSource: 'followUps',
					followUpSource: 'ai-mate',
				},
				deepResearchEnabled: undefined,
				thinkDeeperEnabled: undefined,
				fastModeEnabled: undefined,
				taskModeEnabled: undefined,
				webSearchEnabled: currentChatMode.webSearchEnabled,
				addCurrentPageAsContextUserPreference: currentChatMode.useCurrentPageContext,
				appFilters: currentChatMode.appFilters,
			});
		},
		[
			conversationId,
			conversationAgent,
			storeUserMessages,
			sendMessage,
			currentChatMode,
			sendAnalyticsEvent,
		],
	);

	const isPulseExperimentEnabled = FeatureGates.getExperimentValue(
		'rovo_growth_chat_pulse',
		'isEnabled',
		false,
	);

	const onFollowUpClicked = useCallback(
		(storeChatItem: StoreFollowUp, prompt: string) => {
			let webSearchEnabled: boolean | undefined;
			let deepResearchEnabled: boolean | undefined;
			let thinkDeeperEnabled: boolean | undefined;
			let fastModeEnabled: boolean | undefined;
			let taskModeEnabled: boolean | undefined;
			let useCurrentPageContext: boolean | undefined;
			let appFilters: RovoApp[] | undefined;

			webSearchEnabled = currentChatMode.webSearchEnabled;
			// current deep research mode is not inherited to follow ups as recommended by the product
			deepResearchEnabled = undefined;
			thinkDeeperEnabled = undefined;
			fastModeEnabled = undefined;
			taskModeEnabled = undefined;
			useCurrentPageContext = currentChatMode.useCurrentPageContext;
			appFilters = currentChatMode.appFilters;

			sendMessage({
				agent: conversationAgent,
				prompt: encodeAdf(prompt),
				conversationId,
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages,
				messageSources: {
					interactionSource: 'followUps',
					followUpSource: 'ai-mate',
				},
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				taskModeEnabled,
				webSearchEnabled,
				addCurrentPageAsContextUserPreference: useCurrentPageContext,
				appFilters,
			});

			// The follow-up button is rendered after the originating assistant message has
			// finished streaming, so we can read the executed interaction mode from the
			// stored assistant message via the follow-up's `agentMessageId`.
			const originatingAssistantMessage = storeChatItem.agentMessageId
				? conversationMessages.find(
						(item): item is StoreAssistant =>
							isStoreAssistant(item) &&
							item.content?.message?.message?.id === storeChatItem.agentMessageId,
					)
				: undefined;

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'followUpQueryButtonClicked',
				proactive: false,
				subjectId: 'followUpQueryButton',
				messageId: storeChatItem.agentMessageId,
				singleInstrumentationID: storeChatItem.agentMessageId,
				webSearchEnabled: currentChatMode.webSearchEnabled,
				attributes: {
					...interactionModeExecutedAttributes(
						getInteractionModeExecutedFromStoreAssistant(originatingAssistantMessage),
					),
				},
			});
		},
		[
			conversationId,
			conversationAgent,
			storeUserMessages,
			sendMessage,
			currentChatMode,
			trackAIMAUEvent,
			conversationMessages,
		],
	);

	const renderLoadingState = useMemo(() => {
		const shouldShowGreetingLoading =
			elements?.shouldShowGreeting !== false &&
			(elements?.shouldShowGreeting === true || mode === ConversationAssistantUIModes.SIDEBAR);

		return !fullScreen ? (
			<RovoEmptyStateLoading
				showGreeting={shouldShowGreetingLoading}
				showActions={
					mode === ConversationAssistantUIModes.SIDEBAR ||
					mode === ConversationAssistantUIModes.MINI_MODAL
				}
			/>
		) : (
			<LoadingState />
		);
	}, [fullScreen, mode, elements?.shouldShowGreeting]);

	const renderEmptyState = useEmptyStateRender({
		activeTabId: activeTabIdProp,
		conversation,
		conversationAgent,
		conversationId,
		conversationMessagesResponseCount,
		currentChatMode,
		elements,
		formatMessage,
		handleChatEmptyLaunch,
		handleClickDynamicPrompt,
		isConversationAgentDefault,
		isRenderStabilizationEnabled,
		isResizeEnabled,
		isResizeHeaderThresholdReached,
		mode,
		onActiveTabChange,
		onDiscoverPromptLibraryClick,
		product,
		profileData,
		runtimeCustomGreeting,
		selectedSpace,
		selectedPinnedItem,
		sendMessage,
		siteId,
		storeUserMessages,
	});

	const isCustomAgentResearch = useCallback(
		(messageId: string) => {
			const messageResearch = getMessageResearchById({ messageId });
			if (isMarathonAgent(conversationAgent) && !messageResearch?.isDeepResearch) {
				return false;
			}

			return (
				!isConversationAgentDefault &&
				!!messageResearch &&
				fg('rovo_agent_default_scenario_deep_research_validate')
			);
		},
		[getMessageResearchById, isConversationAgentDefault, conversationAgent],
	);

	const renderResearch = useCallback(
		({
			streamedContent,
			messageId,
			messageHasContent: _messageHasContent,
			forceDeepResearch,
			generatingText,
			answerNowStatus,
			onAnswerNowClick,
			hideLiveThinkingIndicator,
			personalityThinkingText,
		}: {
			streamedContent: string;
			messageId: string;
			messageHasContent: boolean;
			forceDeepResearch?: boolean;
			generatingText?: string;
			answerNowStatus?: AnswerNowStatus;
			onAnswerNowClick?: () => void;
			hideLiveThinkingIndicator?: boolean;
			personalityThinkingText?: string;
		}) => {
			if (currentChatMode.reasoningMode === 'deep-research' || forceDeepResearch) {
				return (
					<MessageDeepResearchV2Refresh
						messageId={messageId}
						generatingText={generatingText}
						personalityThinkingText={personalityThinkingText}
						hideLiveThinkingIndicator={hideLiveThinkingIndicator}
					/>
				);
			}

			if (isCustomAgentResearch(messageId)) {
				return (
					<MessageDeepResearchV2Refresh
						messageId={messageId}
						// Keep research loading aligned with `useStreamingThinkingAgent`.
						agent={thinkingAgent}
						generatingText={generatingText}
						hideLiveThinkingIndicator={hideLiveThinkingIndicator}
					/>
				);
			}

			return (
				<MessageThinkDeeperResearch
					messageId={messageId}
					streamedContent={streamedContent}
					generatingText={generatingText}
					personalityThinkingText={personalityThinkingText}
					answerNowStatus={answerNowStatus}
					onAnswerNowClick={onAnswerNowClick}
					hideLiveThinkingIndicator={hideLiveThinkingIndicator}
				/>
			);
		},
		[currentChatMode.reasoningMode, isCustomAgentResearch, thinkingAgent],
	);

	const renderAgenticUi = useCallback(
		(storeChatItem: StoreAssistant) => {
			if (!fg('jira_ai_rendering_a2ui')) {
				return null;
			}

			const assistantMessageId = storeChatItem.content.message.message.id;

			const agenticUiMessages = storeChatItem.agenticUiMessages;
			if (!agenticUiMessages || agenticUiMessages.length === 0) {
				return null;
			}

			return (
				<ErrorBoundary>
					{/*
					Renders A2UI messages if an agent returns the AGENTIC_UI message type from an SSE stream
				 */}
					{fg('jira_ai_replace_custom_a2ui_renderer') ? (
						<A2uiSurfaceEntryPointRenderer
							messages={agenticUiMessages}
							onAction={(action) => {
								handleA2UIAction(
									{ eventName: action.name, context: action.context },
									assistantMessageId,
								);
							}}
						/>
					) : (
						<A2UISurface
							messages={agenticUiMessages}
							onAction={(action) => {
								handleA2UIAction(
									{ eventName: action.eventName, context: action.context },
									assistantMessageId,
								);
							}}
						/>
					)}
				</ErrorBoundary>
			);
		},
		[handleA2UIAction],
	);

	const stableOlderMessageRenderVersion = useMemo(
		() => ({
			chatMode: {
				deepResearchEnabled: currentChatMode.deepResearchEnabled,
				fastModeEnabled: currentChatMode.fastModeEnabled,
				reasoningMode: currentChatMode.reasoningMode,
				taskModeEnabled: currentChatMode.taskModeEnabled,
				thinkDeeperEnabled: currentChatMode.thinkDeeperEnabled,
				useCurrentPageContext: currentChatMode.useCurrentPageContext,
				webSearchEnabled: currentChatMode.webSearchEnabled,
			},
			conversationId,
			firstSessionAssistantMessageId,
			isConversationAgentDefault,
			lastHumanMessageContentRenderVersion,
			lastHumanMessageId,
			lastSendWasInterrupted,
			mode,
			product,
			recommendationConnectorForRender,
			renderAgenticUi,
			sharedChatContinuationPillTarget,
			shouldEnableAgentResponseHat,
			storeUserMessages,
			thinkingAgentId: thinkingAgent?.id,
			thinkingAgentIsDefault: thinkingAgent?.is_default,
			thinkingAgentName: thinkingAgent?.name,
		}),
		[
			conversationId,
			currentChatMode.deepResearchEnabled,
			currentChatMode.fastModeEnabled,
			currentChatMode.reasoningMode,
			currentChatMode.taskModeEnabled,
			currentChatMode.thinkDeeperEnabled,
			currentChatMode.useCurrentPageContext,
			currentChatMode.webSearchEnabled,
			firstSessionAssistantMessageId,
			isConversationAgentDefault,
			lastHumanMessageContentRenderVersion,
			lastHumanMessageId,
			lastSendWasInterrupted,
			mode,
			product,
			recommendationConnectorForRender,
			renderAgenticUi,
			sharedChatContinuationPillTarget,
			shouldEnableAgentResponseHat,
			storeUserMessages,
			thinkingAgent?.id,
			thinkingAgent?.is_default,
			thinkingAgent?.name,
		],
	);

	const {
		isConversationActivelyGenerating,
		shouldUseStableLatestMessageRenderer,
		stableLatestMessageRenderVersion,
	} = useLatestMessageRenderStabilization({
		conversation,
		isRateLimitedState,
		isThinkingAgentHydrating,
		rateLimitedActionType,
		shouldUseStableOlderMessageRenderer,
		stableOlderMessageRenderVersion,
	});

	const showSkills = hasOpenedSkillsOnceRef.current ? (
		<SkillsInlineBrowse
			screenType={screenType}
			closeInlineBrowse={closeInlineBrowse}
			onSelectSkill={handleSkillSelect}
		/>
	) : null;

	const showPromptLibrary = hasOpenedPromptLibraryOnceRef.current ? (
		<Suspense fallback={<Spinner />} interactionName="skill-metadata-sidebar-prompt-library">
			<SkillMetadataProvider
				siteId={siteId}
				tags={{ skillRegistryTags: [PROMPT_LIBRARY_SKILL_TAG] }}
			>
				<SidebarPromptInlineBrowse
					screenType={screenType}
					conversationId={conversationId}
					closeInlineBrowse={closeInlineBrowse}
				/>
			</SkillMetadataProvider>
		</Suspense>
	) : null;

	if (finalScreenType === 'loading' || finalScreenType === 'empty') {
		if (finalScreenType === 'empty' && activeAction) {
			return (
				<Box xcss={cx(styles.container, fg('rovo_skills_directory_ui') && styles.containerFlex)}>
					{/* for UFO performance tracking dimensions */}
					<UFOCustomData
						data={{
							conversationHistorySubScreen: 'active-action',
							conversationMessagesResponseCount,
						}}
					/>

					{isPulseExperimentEnabled ? <ConversationTitleBlockContainer /> : null}

					<Box
						ref={chatHistoryContainerRef}
						xcss={styles.scrollingDiv}
						testId="chat-history-container"
					>
						<ConversationActionContainer
							action={activeAction}
							mode={mode || ConversationAssistantUIModes.SIDEBAR}
							fullScreen={Boolean(fullScreen)}
							agent={conversationAgent}
							storeUserMessages={storeUserMessages}
							webSearchEnabled={currentChatMode.webSearchEnabled}
							addCurrentPageAsContextUserPreference={currentChatMode.useCurrentPageContext}
							appFilters={currentChatMode.appFilters}
						/>
					</Box>

					{showSkills}
					{showPromptLibrary}
				</Box>
			);
		}

		return (
			<>
				<ExitingPersistence appear exitThenEnter>
					<FadeIn
						key={finalScreenType}
						duration="large"
						entranceDirection="bottom"
						exitDirection="bottom"
						isPaused={!sidebarActionsStaggerAnimation.shouldAnimate && finalScreenType === 'empty'}
					>
						{(props) => (
							<div
								{...props}
								css={[
									fullScreen
										? styles.animationTransitionContainerFS
										: useMiniModalStyles
											? styles.animationTransitionContainerMiniModal
											: styles.animationTransitionContainer,
								]}
							>
								{finalScreenType === 'loading' ? renderLoadingState : renderEmptyState}
							</div>
						)}
					</FadeIn>
				</ExitingPersistence>
				{finalScreenType === 'loading' && <UFOLoadHold name="rovo-chat-history" />}

				{showSkills}
				{showPromptLibrary}
			</>
		);
	}

	if (!conversation) {
		return null;
	}

	const { traces, status } = conversation;

	// Declared before the render callbacks (`renderMessage`, `renderTracesAndErrors`)
	// that consume it. `const` is not hoisted, so keeping this above its callers avoids
	// a temporal-dead-zone ReferenceError if a caller is ever invoked synchronously.
	const getThinkingIndicatorHasHandedOff = ({
		hasThinkDeeperTriggered,
		isResearchStoreInitialized,
		researchCompleted = false,
	}: {
		hasThinkDeeperTriggered: boolean;
		isResearchStoreInitialized: boolean;
		researchCompleted?: boolean;
	}) =>
		!traces?.length &&
		(!hasThinkDeeperTriggered || researchCompleted) &&
		(isResearchStoreInitialized || status === CHAT_STORE_STATUS.idle);

	const shouldShowConversationHistoryWarning =
		Math.floor(
			(Date.now().valueOf() - new Date(conversation.timeCreated).valueOf()) / (1000 * 60 * 60 * 24),
		) > 28;

	const renderTracesAndErrors = (
		hasThinkDeeperTriggered: boolean,
		isResearchStoreInitialized: boolean,
		thinkingText: string,
		researchCompleted: boolean = false,
		forceHideThinkingIndicator: boolean = false,
	) => {
		const shouldHideThinkingWhenNoTraces = getThinkingIndicatorHasHandedOff({
			hasThinkDeeperTriggered,
			isResearchStoreInitialized,
			researchCompleted,
		});
		return (
			<>
				<MessageLoadingPaddedContainer>
					<MessageLoading
						agent={!isThinkingAgentDefault ? thinkingAgent : undefined}
						traces={tracesForLoading}
						thinkingText={thinkingText}
						// Hide while routed-author metadata hydrates, or when there are no traces / active research steps to show.
						hideThinkingIndicator={
							forceHideThinkingIndicator ||
							isThinkingAgentHydrating ||
							shouldHideThinkingWhenNoTraces
						}
						animation={true}
					/>
				</MessageLoadingPaddedContainer>
				{isRateLimitedState && (
					<SlideIn fade="in" animationTimingFunction="ease-out" enterFrom={'top'} duration="medium">
						{(animationProps) => (
							<Box {...animationProps}>
								{rateLimitedActionType === 'deep-research' ? (
									<StoreDeepResearchRateLimitWarning
										removeRateLimiting={() => setConversationError(undefined)}
									/>
								) : (
									<StoreRateLimitWarning
										removeRateLimiting={() => setConversationError(undefined)}
									/>
								)}
							</Box>
						)}
					</SlideIn>
				)}
			</>
		);
	};

	const messageRenderers: ChatHistoryMessageRenderers = {
		human: (storeChatItem, { index }) => {
			const key = `history-message-${index}`;
			const isEditableHumanMessage =
				String(storeChatItem.content.id) === lastHumanMessageId &&
				(storeChatItem.content.parent_message_id || lastSendWasInterrupted) &&
				isEditLastUserPromptEnabled;

			// Only the editable human bubble (the last one with a parent id, or after
			// a send that didn't complete - user Stop, network error, or
			// browser-killed - when the flag is on) gets the edit affordance.
			// Earlier bubbles in history pass `undefined`.
			const onEditAndResend = isEditableHumanMessage ? handleEditAndResendMessage : undefined;

			const respondedAgentId = respondedAgentIdByHumanMessageId.get(
				String(storeChatItem.content.id),
			);
			if (fg('rovo_chat_history_lift')) {
				return (
					<StoreHumanView
						key={key}
						interactionMode="live"
						storeHuman={storeChatItem}
						onEditAndResend={onEditAndResend}
						respondedAgentId={respondedAgentId}
					/>
				);
			}

			if (storeChatItem.redactedPrompt && fg('rovo-guard-ui-redaction-and-blocking')) {
				return (
					<>
						<StoreHumanView
							key={key}
							storeHuman={storeChatItem}
							onEditAndResend={onEditAndResend}
							respondedAgentId={respondedAgentId}
						/>
						<RedactionEnforcementMessage />
					</>
				);
			}
			return (
				<StoreHumanView
					key={key}
					storeHuman={storeChatItem}
					onEditAndResend={onEditAndResend}
					respondedAgentId={respondedAgentId}
				/>
			);
		},
		assistant: (
			storeChatItem,
			{ index, isLastAssistant, shouldRenderLiveAssistantAffordances },
		) => {
			const key = `history-message-${index}`;
			const messageId = storeChatItem.content.message.message.id;
			const shouldRenderLiveAssistantState =
				shouldRenderLiveAssistantAffordances && isLastAssistant;
			// `getRovoThinkingState` runs `getMessageResearchById`, `formatMessage` and
			// feature-gate checks, and every consumer below (the thinking indicator and
			// `traceRender`) is only relevant for the last assistant message. Skip the work
			// entirely for earlier messages so long conversations don't recompute it for
			// every assistant bubble on each render.
			// A non-last-assistant message should still render its research traces if
			// the research store has data for it (think-deeper / implicit research steps).
			// Use the memoized Set for a cheap O(1) existence check, then only call
			// getMessageResearchById (full record) when we actually need to render state.
			const hasResearchStoreData = messageIdsWithResearchData.has(messageId);
			const shouldComputeThinkingState = isLastAssistant || hasResearchStoreData;
			const messageResearchData = shouldComputeThinkingState
				? getMessageResearchById({ messageId })
				: null;
			const rovoThinkingState = shouldComputeThinkingState
				? getRovoThinkingState(storeChatItem, messageResearchData)
				: undefined;
			const isAssistantActivelyGenerating =
				shouldRenderLiveAssistantState &&
				isConversationActivelyGenerating &&
				storeChatItem.content.streamingComplete !== true;
			// Hide the live thinking indicator when:
			// 1. Virtualization is active and the message is not actively generating
			//    (prevents a flash of the thinking indicator when reopening a completed conversation), OR
			// 2. The message's streaming is already complete — this prevents the thinking indicator
			//    from briefly re-appearing between when traces are cleared (on FINAL_RESPONSE) and
			//    when the store status transitions to idle.
			// Read hasReceivedAnswerPart from the chat store message — set atomically with
			// clearTracesIfNotEmpty so both flags update in the same store dispatch, preventing
			// the cross-store render gap that caused the thinking indicator to briefly reappear.
			const shouldHideLiveThinkingIndicator = computeShouldHideLiveThinkingIndicator({
				isVirtualizationEnabled,
				isAssistantActivelyGenerating,
				streamingComplete: storeChatItem.content.streamingComplete,
				hasReceivedAnswerPart: storeChatItem.hasReceivedAnswerPart,
			});
			// The response hat succeeds the "{agent} is thinking" indicator: it is shown only
			// once the thinking traces have handed off (even while the answer text is still
			// streaming), so there is no gap or overlap between the two affordances. Earlier
			// messages have no live thinking indicator, so they are treated as already handed
			// off and keep their author-transition hats.
			const shouldShowThinkingTraces = Boolean(
				shouldRenderLiveAssistantState && rovoThinkingState && !shouldHideLiveThinkingIndicator,
			);
			const canRenderAfterThinking = !shouldShowThinkingTraces;
			const previousAssistantAuthorFromHistory = shouldEnableAgentResponseHat
				? previousAssistantAuthorByMessageId.get(messageId)
				: undefined;
			// The first streamed assistant row has no previous assistant message yet, but
			// it still needs a baseline to decide whether a selected-agent response hat is
			// meaningful before the first answer token arrives. Use the conversation agent
			// only for the actively generating row so completed first responses keep the
			// historical "no previous assistant" behaviour.
			const previousAssistantAuthor =
				previousAssistantAuthorFromHistory ??
				(shouldEnableAgentResponseHat && isAssistantActivelyGenerating
					? deriveBaselineAuthorIdentity(conversationAgent)
					: undefined);

			const messageHasContent = Boolean(
				storeChatItem.content.message.message.content?.toString().trim(),
			);
			const isDeepResearchInProgress =
				storeChatItem.content.message.message.message_metadata?.deep_research;
			const generatingText = hasStreamingAction(storeChatItem)
				? formatMessage(messages.agentIsGenerating, {
						name: DEFAULT_AGENT_DISPLAY_NAME,
					})
				: undefined;

			// Compute trace render only for the last assistant message in the group
			// (`rovoThinkingState` is only defined when `isLastAssistant`).
			const traceRender = rovoThinkingState
				? (() => {
						const thinkingDisplayText = generatingText ?? rovoThinkingState.thinkingText;
						return renderTracesAndErrors(
							rovoThinkingState.hasThinkDeeperTriggered,
							rovoThinkingState.isResearchStoreInitialized,
							thinkingDisplayText,
							rovoThinkingState.researchCompleted,
							shouldHideLiveThinkingIndicator,
						);
					})()
				: null;
			const shouldRenderActiveResponseHatHandoff =
				shouldEnableAgentResponseHat && isAssistantActivelyGenerating;
			return (
				<Box key={key} data-scroll-anchor="assistant-message" data-message-id={messageId}>
					<AnalyticsContext
						data={{
							source: 'AIMate',
							attributes: {
								messageId,
								conversationId,
							},
						}}
					>
						{shouldRenderLiveAssistantAffordances ? (
							<HideWhenThinkDeeperInProgress messageId={messageId}>
								{traceRender}
							</HideWhenThinkDeeperInProgress>
						) : null}

						{shouldRenderActiveResponseHatHandoff ? (
							<ActiveResponseHatHandoff
								storeAssistant={storeChatItem}
								previousAssistantAuthor={previousAssistantAuthor}
								shouldEnableAgentResponseHat={shouldEnableAgentResponseHat}
								isConversationActivelyGenerating={isConversationActivelyGenerating}
								canRenderAfterThinking={canRenderAfterThinking}
								hasAnswerContent={messageHasContent}
							/>
						) : null}

						{storeChatItem.memory &&
							FeatureGates.getExperimentValue('memory_management_ux', 'isEnabled', false, {
								fireExperimentExposure: false,
							}) && (
								<StoreMemoryUpdateView
									storeMemoryUpdate={storeChatItem.memory}
									onOpenMemoryManagement={() => {
										sendAnalyticsEvent({
											action: 'clicked',
											actionSubject: 'button',
											actionSubjectId: 'reviewMemoryButton',
										});
										onOpenMemoryManagement?.();
									}}
								/>
							)}

						{renderResearch({
							streamedContent: storeChatItem.content.message.message.content?.toString(),
							messageId,
							messageHasContent,
							forceDeepResearch: isDeepResearchInProgress,
							generatingText,
							personalityThinkingText,
							answerNowStatus:
								shouldRenderLiveAssistantState && !storeChatItem.content.streamingComplete
									? storeChatItem.answerNowStatus
									: undefined,
							hideLiveThinkingIndicator: shouldHideLiveThinkingIndicator,
							onAnswerNowClick:
								shouldRenderLiveAssistantState &&
								!storeChatItem.content.streamingComplete &&
								isAnswerNowAvailable(storeChatItem.answerNowStatus) &&
								conversation
									? () => {
											const status = storeChatItem.answerNowStatus;
											if (isAnswerNowAvailable(status)) {
												answerNow({
													conversationId: conversation.id,
													tmpMessageId: status.tmpMessageId,
													remoteMessageId: status.remoteMessageId,
												});
											}
										}
									: undefined,
						})}

						{renderAgenticUi(storeChatItem)}

						<ViewDeadAirTracker id={`answer-${messageId}`} contentType="stream">
							<StoreAssistantView
								storeAssistant={storeChatItem}
								previousAssistantAuthor={previousAssistantAuthor}
								isConversationActivelyGenerating={isConversationActivelyGenerating}
								thinkingIndicatorHasHandedOff={canRenderAfterThinking}
								suppressInternalResponseHat={shouldRenderActiveResponseHatHandoff}
								onAuthLinkClicked={(authType) => handleAuthLinkClicked(authType, messageId)}
								onFollowUpClick={onInlineFollowUpClicked}
								{...(fg('rovo_chat_history_lift') && { interactionMode: 'live' as const })}
								mode={mode}
								product={product}
								{...(fg('jira-ai-agentic-planning-technical-changes') && {
									isLastMessage: isLastAssistant,
								})}
								{...(fg('jsm-kb-action-message-prop') && {
									isLastActionMessage: isLastAssistant,
								})}
							/>
						</ViewDeadAirTracker>
						{storeChatItem.content.message.message.id === firstSessionAssistantMessageId &&
							storeChatItem.content.streamingComplete &&
							recommendationConnectorForRender !== null && (
								// Use the latched connector so the card stays mounted (and pinned to
								// the same connector) for the rest of this render lifecycle.
								<StoreConnectorRecommendation
									connector={recommendationConnectorForRender}
									lastHumanMessage={lastHumanMessage?.content}
									handleResendMessage={handleResendMessage}
								/>
							)}
						{storeChatItem.content.message.message.id === firstSessionAssistantMessageId &&
							storeChatItem.content.streamingComplete &&
							appConnectionPromptAppsForRender !== null && (
								// 3P suggested-prompts connection prompt (CCINT-8723), rendered inline
								// after the first response completes. Uses the latched apps so it stays
								// mounted across new messages (mirrors StoreConnectorRecommendation).
								<InThreadAppConnectionPrompt apps={appConnectionPromptAppsForRender} />
							)}
					</AnalyticsContext>
				</Box>
			);
		},
		followUp: (storeChatItem, { index }) => {
			const key = `history-message-${index}`;
			return (
				<Box key={key} data-scroll-anchor="follow-up-message">
					<StoreFollowUpView
						followUp={storeChatItem}
						{...(fg('rovo_chat_history_lift') && { interactionMode: 'live' as const })}
						onFollowUpClick={(prompt) => {
							onFollowUpClicked(storeChatItem, prompt);
						}}
					/>
				</Box>
			);
		},
		guardBlock: (storeChatItem, { index }) => {
			const key = `history-message-${index}`;
			return fg('rovo-guard-ui-redaction-and-blocking') ? (
				<Box key={key} data-scroll-anchor="guard-block-message">
					<StoreGuardBlockView
						{...(fg('rovo_chat_history_lift') && { interactionMode: 'live' as const })}
						traceId={storeChatItem.traceId}
						blockType={storeChatItem.blockType}
					/>
				</Box>
			) : null;
		},
		error: (storeChatItem, { index }) => {
			const key = `history-message-${index}`;
			const lastSentMessage = lastHumanMessage?.content.content;
			return (
				<Box key={key} data-scroll-anchor="error-message">
					<StoreErrorView
						{...(fg('rovo_chat_history_lift') && { interactionMode: 'live' as const })}
						error={storeChatItem}
						onResendMessage={
							lastSentMessage
								? () => handleResendMessage(lastSentMessage, storeChatItem)
								: undefined
						}
					/>
				</Box>
			);
		},
	};

	const renderMessageWithSharedChatContinuationPill = (
		storeChatItem: StoreChatItem,
		i: number,
		renderedMessage: React.ReactNode,
	) => {
		if (!renderedMessage || storeChatItem !== sharedChatContinuationPillTarget) {
			return renderedMessage;
		}

		return (
			<React.Fragment key={`history-message-${i}`}>
				{renderedMessage}
				<SharedChatContinuationPill />
			</React.Fragment>
		);
	};

	return (
		<ChatTranscript
			fullScreen={fullScreen}
			isFullScreenErrorState={isFullScreenErrorState}
			isPulseExperimentEnabled={isPulseExperimentEnabled}
			liveRegionText={liveRegionText}
			mode={mode}
			shouldShowConversationHistoryWarning={shouldShowConversationHistoryWarning}
			conversationMessagesResponseCount={conversationMessagesResponseCount}
			conversationHistoryWarningMessage={formatMessage(messages.conversationLimit)}
			showSkills={showSkills}
			showPromptLibrary={showPromptLibrary}
			chatHistoryContainerRef={chatHistoryContainerRef}
			messagePresentation={{
				renderers: messageRenderers,
				decorateMessage: renderMessageWithSharedChatContinuationPill,
				default: {
					shouldRenderLiveAssistantAffordances: true,
				},
				latest: {
					shouldRenderLiveAssistantAffordances: true,
					...(shouldUseStableLatestMessageRenderer && {
						renderVersion: stableLatestMessageRenderVersion,
					}),
				},
				older: {
					shouldRenderLiveAssistantAffordances: !shouldUseStableOlderMessageRenderer,
					...(shouldUseStableOlderMessageRenderer && {
						renderVersion: stableOlderMessageRenderVersion,
					}),
				},
			}}
			messageGroupsContentProps={{
				conversationId,
				messageGroups,
				olderMessageGroups,
				isVirtualizationEnabled,
				isRenderStabilizationEnabled,
				chatHistoryContainerRef,
				isScrollPending: !!pendingScrollToMessageIdRef.current,
				lastMessageGroupRef,
				scrollToBottomGeneration,
			}}
		/>
	);
};
